import axios from 'axios'
import {
  IMessageResponseDto,
  ILoginResponse,
  LoginUserDto,
  VerifySignupResponse,
  ResetDto,
  IResetPasswordDto,
  IForgotPasswordDto,
  IFinishUserSignupDto,
  IFinishUserSignupResponse,
  IUserAuthDto,
  ISignInEmailVerificationDto,
  ISignInEmailVerificationResponse,
  IVerifyEmailResponse,
  IConfirmLoginByProviderResponse,
  IConfirmLoginByProviderDto,
  ISignupUserRespDto,
  IForgotPasswordRespDto,
  IRefreshTokenResponse,
  ILoginUserWeb3Dto,
  ISend2FAVerificationEmailDto,
  I2FAVerificationDto,
  I2FAVerificationResponse,
  IWeb3LoginResponse,
  IUserSignupDto,
  IVerifyResetPasswordResponse,
  ISignupAccountCreateCheckDto,
  IAccountCreateCheckRespDto,
} from 'src/types'
import { HttpService } from './http.service'
import { STORAGE_KEYS } from 'src/constants'

class AuthService extends HttpService {
  constructor() {
    super(axios)
  }

  async refreshToken() {
    if (!localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
      return null
    }

    return this.put<IRefreshTokenResponse, null>(
      `auth/refresh/${localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)}`,
      null,
    )
  }

  login(dto: LoginUserDto) {
    return this.post<ILoginResponse, LoginUserDto>('auth/signin', dto)
  }

  loginWeb3(dto: ILoginUserWeb3Dto) {
    return this.post<IWeb3LoginResponse, ILoginUserWeb3Dto>('auth/signin/web3', dto)
  }

  signup(dto: IUserSignupDto) {
    return this.post<ISignupUserRespDto, IUserSignupDto>('auth/register', dto)
  }

  signupAccountExistCheck(dto: ISignupAccountCreateCheckDto) {
    return this.post<IAccountCreateCheckRespDto, ISignupAccountCreateCheckDto>(
      'auth/register/check',
      dto,
    )
  }

  verifySignUpEmail(token: string, code: string, referrerId?: string | null) {
    let url = `auth/verification/${token}?code=${code}`
    if (referrerId) {
      url += `&referrer_id=${referrerId}`
    }
    return this.put<VerifySignupResponse, null>(url, null)
  }

  verifyEmail(token: string, code: string) {
    return this.put<IVerifyEmailResponse, null>(
      `auth/emails/verification/${token}?code=${code}`,
      null,
    )
  }

  forgotPassword(dto: IForgotPasswordDto) {
    return this.post<IForgotPasswordRespDto, IForgotPasswordDto>('auth/forgot-password', dto)
  }

  verifyResetPassword(token: string, code: string) {
    return this.put<IVerifyResetPasswordResponse, null>(
      `auth/forgot-password/${token}?code=${code}`,
      null,
    )
  }

  resetPassword({ password, token }: IResetPasswordDto) {
    return this.post<IMessageResponseDto, ResetDto>(`auth/reset-password/${token}`, { password })
  }

  finishSignup(dto: IFinishUserSignupDto, token?: string, referrerId?: string | null) {
    let params: {} = { token }

    if (referrerId) {
      params = { ...params, referrer_id: referrerId }
    }

    return this.put<IFinishUserSignupResponse, IFinishUserSignupDto>(
      'auth/register/agreement',
      dto,
      {
        params,
      },
    )
  }

  sendNewEmailVerifiaction(dto: ISignInEmailVerificationDto) {
    return this.put<ISignInEmailVerificationResponse, ISignInEmailVerificationDto>(
      'auth/emails/send-new-verification',
      dto,
    )
  }

  sendEmailVerification(dto: ISignInEmailVerificationDto) {
    return this.put<ISignInEmailVerificationResponse, ISignInEmailVerificationDto>(
      'auth/emails/send-verification',
      dto,
    )
  }

  async checkIsAuthenticated() {
    const userAuth = await this.get<IUserAuthDto>('auth', {
      headers: { Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)}` },
    })
    if (!userAuth) {
      return this.refreshToken()
    }
    return userAuth
  }

  confirmLoginByProvider(dto: IConfirmLoginByProviderDto) {
    return this.post<IConfirmLoginByProviderResponse, IConfirmLoginByProviderDto>(
      'auth/providers/signin-confirm',
      dto,
    )
  }

  send2FAVerificationEmail(dto: ISend2FAVerificationEmailDto) {
    return this.post<IMessageResponseDto, ISend2FAVerificationEmailDto>('auth/2fa/emails/send', dto)
  }

  get2FAVerificationEmailTime(userId: string) {
    return this.get<{
      status: boolean
      time: Date
    }>(`auth/2fa/emails/time/${userId}`)
  }

  getAuthToken(token: string) {
    return this.get<any>(`auth/2fa/emails/token/${token}`)
  }

  verify2FA(dto: I2FAVerificationDto) {
    return this.post<I2FAVerificationResponse, I2FAVerificationDto>('auth/2fa/verify', dto)
  }

  async validateToken(token: string) {
    return this.get<{ status: boolean }>(`token-vault/token-validate/${token}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)}` },
    })
  }

  async deleteTokens(accessToken: string, refreshToken: string) {
    await this.delete<void>(`token-vault/token/${refreshToken}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    await this.delete<void>(`token-vault/token/${accessToken}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  }
}

const AuthDataService = new AuthService()

export default AuthDataService
